import React from 'react';

const Projects: React.FC = () => {
  const projects = [
    {
      title: 'Appliance Repair Company Website',
      description: 'A powerful Next.js and API integrated website for an appliance repair company. Features modern design, dynamic content, service scheduling, and seamless user experience with responsive layouts.',
      imageGradient: 'from-indigo-500 to-purple-600',
      icon: 'fas fa-tools',
      technologies: ['Next.js', 'React', 'API Integration', 'TypeScript', 'Tailwind CSS'],
      category: 'web',
      github: 'https://github.com/SilentMic',
      demo: 'https://your-appliance-repair-link.com',
      featured: true
    },
    {
      title: 'Brew & Bloom Coffee Shop',
      description: 'Modern coffee shop website featuring an elegant design with smooth animations, responsive layout, and interactive elements. Built with React and styled with Tailwind CSS for a premium user experience.',
      imageGradient: 'from-amber-500 to-orange-600',
      icon: 'fas fa-coffee',
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Responsive Design'],
      category: 'web',
      github: 'https://github.com/SilentMic',
      demo: 'https://coffee-shop-phi-ivory.vercel.app',
      featured: true
    },
    {
      title: 'WebSight SEO',
      description: 'Advanced SEO analysis tool that provides comprehensive website performance insights, detailed metrics, and actionable recommendations to improve search engine rankings and organic traffic.',
      imageGradient: 'from-blue-500 to-cyan-600',
      icon: 'fas fa-chart-line',
      technologies: ['HTML', 'CSS', 'JavaScript', 'SEO APIs', 'Vercel'],
      category: 'web',
      github: 'https://github.com/SilentMic/WebSight-SEO',
      demo: 'https://seo-rating-app.vercel.app',
      featured: true
    },
    {
      title: 'TaskManager',
      description: 'C# and SQL console-based application using Entity Framework Core and SQLite, featuring CRUD operations for tasks with categories, priorities, due dates, and an interactive calendar.',
      imageGradient: 'from-purple-500 to-pink-600',
      icon: 'fas fa-tasks',
      technologies: ['C#', 'Entity Framework Core', 'SQLite', 'Console Application'],
      category: 'fullstack',
      github: 'https://github.com/SilentMic/TaskManager',
      demo: 'https://github.com/SilentMic/TaskManager',
      featured: false
    },
    {
      title: 'Weather App',
      description: 'Interactive Weather App using HTML, CSS, and JavaScript with OpenWeatherMap API. Includes real-time weather updates, and dynamic day/night themes.',
      imageGradient: 'from-sky-400 to-blue-600',
      icon: 'fas fa-cloud-sun',
      technologies: ['HTML', 'CSS', 'JavaScript', 'OpenWeatherMap API'],
      category: 'web',
      github: 'https://github.com/SilentMic/WeatherApp',
      demo: 'https://github.com/SilentMic/WeatherApp',
      featured: false
    },
    {
      title: 'Rock Paper Scissors',
      description: 'Developed an interactive Rock Paper Scissors game with modern UI/UX using HTML, CSS, Bootstrap, JavaScript and font awesome.',
      imageGradient: 'from-green-500 to-emerald-600',
      icon: 'fas fa-hand-rock',
      technologies: ['HTML', 'CSS', 'Bootstrap', 'JavaScript', 'Font Awesome'],
      category: 'web',
      github: 'https://github.com/SilentMic/RockPaperScissorWebApp',
      demo: 'https://silentmic.github.io/RockPaperScissorWebApp/',
      featured: false
    }
  ];

  return (
    <section id="projects" className="py-20 bg-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 fade-in">
            My <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto fade-in">
            Here are some of the projects I've worked on. Each project represents a unique challenge and learning experience.
          </p>
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <div
              key={index}
              className="project-card bg-slate-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-700/50 fade-in hover:border-primary/50 transition-all duration-300 flex flex-col"
              style={{ minHeight: '480px' }}
            >
              {/* Project Image with Gradient and Icon */}
              <div className={`relative h-48 bg-gradient-to-br ${project.imageGradient} flex items-center justify-center overflow-hidden flex-shrink-0`}>
                <div className="absolute inset-0 bg-black/20"></div>
                <i className={`${project.icon} text-7xl text-white/90 relative z-10`}></i>
                {project.featured && (
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold border border-white/30">
                    <i className="fas fa-star mr-1"></i>
                    Featured
                  </div>
                )}
              </div>

              {/* Project Content */}
              <div className="p-5 md:p-6 flex flex-col flex-grow">
                <div className="mb-4 flex-grow">
                  <h3 className="text-xl font-bold text-white mb-3 min-h-[56px] flex items-start">{project.title}</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed text-sm min-h-[84px]">{project.description}</p>

                  {/* Technologies */}
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="tech-tag bg-slate-700/50 text-gray-300 px-3 py-1 rounded-full text-xs font-medium border border-slate-600/50 hover:bg-primary/20 hover:border-primary/50 hover:text-primary transition-all duration-300"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Project Links */}
                <div className="border-t border-slate-600/50 pt-4 mt-auto">
                  <div className="grid grid-cols-2 gap-3">
                    <a 
                      href={project.github} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center bg-slate-700/50 text-white text-center py-3 px-4 rounded-lg hover:bg-slate-600 transition-all duration-300 font-medium text-sm border border-slate-600/50 hover:border-slate-500"
                    >
                      <i className="fab fa-github mr-2"></i>
                      Code
                    </a>
                    <a 
                      href={project.demo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center bg-primary text-white text-center py-3 px-4 rounded-lg hover:bg-primary/90 transition-all duration-300 font-medium text-sm"
                    >
                      <i className="fas fa-external-link-alt mr-2"></i>
                      Demo
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* GitHub CTA */}
        <div className="text-center mt-16 fade-in">
          <div className="bg-slate-800/30 backdrop-blur-sm p-8 rounded-xl border border-slate-700/50">
            <h3 className="text-2xl font-bold text-white mb-4">
              <i className="fab fa-github text-primary mr-2"></i>
              Want to see more?
            </h3>
            <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
              Check out my GitHub profile for more projects, contributions, and code samples. 
              I'm always working on something new and exciting!
            </p>
            <a
              href="https://github.com/SilentMic"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 transform hover:scale-105"
            >
              <i className="fab fa-github mr-3 text-xl"></i>
              Visit My GitHub
            </a>
          </div>
        </div>

        {/* Project Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 fade-in">
          {[
            { number: '20+', label: 'Projects Completed', icon: 'fas fa-project-diagram' },
            { number: '15+', label: 'Technologies', icon: 'fas fa-code' },
            { number: '5000+', label: 'Lines of Code', icon: 'fas fa-file-code' },
            { number: '100%', label: 'Satisfaction', icon: 'fas fa-heart' }
          ].map((stat, index) => (
            <div
              key={index}
              className="stat-card bg-slate-800/30 backdrop-blur-sm p-6 rounded-xl text-center border border-slate-700/50 hover:bg-slate-800/50"
            >
              <div className="text-primary text-3xl mb-3">
                <i className={stat.icon}></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">{stat.number}</div>
              <div className="text-gray-400 text-sm font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;